import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { Button } from "@/shared/components/form/Button";
import { InputText } from "@/shared/components/form/InputText";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type {
  UpdateOrganizationFormData,
  UpdateOrganizationRequest,
} from "../types/organization-form.types";
import {
  OrganizationStatus,
  Provider,
} from "../types/organizations-api.types";
import { OrganizationsService } from "../services/organizationsService";
import useTranslations from "@/shared/lang/useTranslations";

interface UpdateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string, data: UpdateOrganizationRequest) => Promise<void>;
  organizationId: string | null;
  isLoading?: boolean;
}

type SelectOption = {
  label: string;
  value: string;
};

export const UpdateOrganizationModal = ({
  isOpen,
  onClose,
  onSubmit,
  organizationId,
  isLoading = false,
}: UpdateOrganizationModalProps) => {
  const { t } = useTranslations("cards");
  const [formData, setFormData] = useState<UpdateOrganizationFormData>({
    name: "",
    slug: "",
    email: "",
    phone: "",
    status: OrganizationStatus.ACTIVE,
    card_provider: Provider.BLOQUE,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isLoadingData, setIsLoadingData] = useState(false);

  const cardProviderOptions: SelectOption[] = useMemo(
    () => [
      { label: t("organizations_provider_bloque"), value: Provider.BLOQUE },
      { label: t("organizations_provider_crypto_mate"), value: Provider.CRYPTO_MATE },
    ],
    [t]
  );

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { label: t("organizations_status_active"), value: OrganizationStatus.ACTIVE },
      { label: t("organizations_status_inactive"), value: OrganizationStatus.INACTIVE },
      { label: t("organizations_status_suspended"), value: OrganizationStatus.SUSPENDED },
      { label: t("organizations_status_pending"), value: OrganizationStatus.PENDING },
    ],
    [t]
  );

  const fetchOrganizationData = useCallback(async () => {
    if (!organizationId) return;

    setIsLoadingData(true);
    try {
      const response = await OrganizationsService.getOrganizationById(organizationId);
      const organization = response.data;

      setFormData({
        name: organization.name || "",
        slug: organization.slug || "",
        email: organization.email || "",
        phone: organization.phone || "",
        status: organization.status || OrganizationStatus.ACTIVE,
        card_provider: organization.card_provider || Provider.BLOQUE,
      });
    } catch (err) {
      console.error(t("update_organization_error_load_data"), err);
    } finally {
      setIsLoadingData(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchOrganizationData();
    } else {
      setFormData({
        name: "",
        slug: "",
        email: "",
        phone: "",
        status: OrganizationStatus.ACTIVE,
        card_provider: Provider.BLOQUE,
      });
      setErrors({});
      setTouched({});
    }
  }, [isOpen, organizationId, fetchOrganizationData]);

  const handleFieldChange = (
    field: keyof UpdateOrganizationFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const normalizePhone = (phone: string): string => {
    if (!phone) return phone;
    return phone.replace(/[\s\-().+]/g, "");
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("update_organization_validation_name_required");
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("update_organization_validation_name_min_length");
    } else if (formData.name.trim().length > 255) {
      newErrors.name = t("update_organization_validation_name_max_length");
    }

    if (formData.slug && formData.slug.length > 255) {
      newErrors.slug = t("update_organization_validation_slug_max_length");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("update_organization_validation_email_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("update_organization_validation_email_invalid");
    } else if (formData.email.length > 255) {
      newErrors.email = t("update_organization_validation_email_max_length");
    }

    if (formData.phone && formData.phone.length > 20) {
      newErrors.phone = t("update_organization_validation_phone_max_length");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationId) return;

    const allFields = Object.keys(formData) as Array<keyof UpdateOrganizationFormData>;
    const newTouched: Record<string, boolean> = {};
    allFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    if (!validateForm()) {
      return;
    }

    const normalizedPhone = formData.phone ? normalizePhone(formData.phone) : null;

    const payload: UpdateOrganizationRequest = {
      name: formData.name.trim(),
      slug: formData.slug.trim() || null,
      email: formData.email.trim(),
      phone: normalizedPhone || null,
      status: formData.status,
      card_provider: formData.card_provider,
    };

    await onSubmit(organizationId, payload);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      slug: "",
      email: "",
      phone: "",
      status: OrganizationStatus.ACTIVE,
      card_provider: Provider.BLOQUE,
    });
    setErrors({});
    setTouched({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("update_organization_modal_title")}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isLoading || isLoadingData}>
            {t("update_organization_modal_button_cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isLoading || isLoadingData}
            disabled={isLoading || isLoadingData}
          >
            {t("update_organization_modal_button_update")}
          </Button>
        </>
      }
    >
      {isLoadingData ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-green-500" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 max-h-[75vh] overflow-y-auto px-2 py-2">
          <div className="space-y-4">
            <div className="border-b border-gray-200 dark:border-zinc-700 pb-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t("update_organization_modal_description")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputSelect<SelectOption>
                label={t("update_organization_label_card_provider")}
                name="card_provider"
                options={cardProviderOptions}
                value={
                  cardProviderOptions.find((opt) => opt.value === formData.card_provider) ||
                  null
                }
                onChange={(selected) => {
                  const value = Array.isArray(selected)
                    ? selected[0]?.value
                    : selected?.value;
                  handleFieldChange("card_provider", value || Provider.BLOQUE);
                }}
                errorMessage={
                  touched.card_provider && errors.card_provider
                    ? errors.card_provider
                    : false
                }
              />

              <InputSelect<SelectOption>
                label={t("update_organization_label_status")}
                name="status"
                options={statusOptions}
                value={
                  statusOptions.find((opt) => opt.value === formData.status) || null
                }
                onChange={(selected) => {
                  const value = Array.isArray(selected)
                    ? selected[0]?.value
                    : selected?.value;
                  handleFieldChange("status", value || OrganizationStatus.ACTIVE);
                }}
                errorMessage={
                  touched.status && errors.status ? errors.status : false
                }
              />

              <InputText
                label={t("update_organization_label_name")}
                name="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                onBlur={() => handleFieldBlur("name")}
                errorMessage={touched.name && errors.name ? errors.name : false}
                required
                maxLength={255}
              />

              <InputText
                label={t("update_organization_label_email")}
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                onBlur={() => handleFieldBlur("email")}
                errorMessage={touched.email && errors.email ? errors.email : false}
                required
                maxLength={255}
              />

              <InputText
                label={t("update_organization_label_slug")}
                name="slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleFieldChange("slug", e.target.value)}
                onBlur={() => handleFieldBlur("slug")}
                errorMessage={touched.slug && errors.slug ? errors.slug : false}
                maxLength={255}
              />

              <InputText
                label={t("update_organization_label_phone")}
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                onBlur={() => handleFieldBlur("phone")}
                errorMessage={touched.phone && errors.phone ? errors.phone : false}
                maxLength={20}
                infoMessage={t("update_organization_info_phone")}
              />
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};
